/**
 * Reservation Mappers for CarBooking Module
 * Funciones para transformar datos de reservas del backend
 */

import { formatDateLocal, toDateWithTime } from './dateHelpers';
import { getEstadoInfo } from './reservationConstants';

/**
 * Parsea un valor JSON de ubicación (origen/destino)
 * @param {string|Object} value - Valor a parsear (puede ser JSON string o objeto)
 * @returns {Object} {address: string, location: {lat, lng}|null}
 */
export function parseJsonLocation(value) {
    if (!value) return { address: "", location: null };
    try {
        const v = typeof value === "string" ? JSON.parse(value) : value;
        if (v && typeof v === "object") {
            return {
                address: v.address || v.direccion || "",
                location: v.location || null,
            };
        }
    } catch {
        // Si viene texto plano, lo usamos como address
        return { address: String(value || ""), location: null };
    }
    return { address: "", location: null };
}

/**
 * Mapea una reserva del backend a un evento de calendario
 * @param {Object} r - Reserva del backend
 * @returns {Object} Evento formateado para el calendario
 */
export function mapReservaToEvent(r) {
    const id = r.idReserva ?? r.IdReserva ?? r.id ?? r.Id ?? 0;

    // --- 1) Normalizar fecha: usar solo YYYY-MM-DD, ignorando la hora ---
    const normalizeDate = (val) => {
        if (!val) return formatDateLocal(new Date());

        if (val instanceof Date) {
            return formatDateLocal(val);
        }

        const s = String(val);
        return s.slice(0, 10);
    };

    const rawFechaInicio =
        r.fechaInicio ??
        r.FechaInicio ??
        r.fecha ??
        r.Fecha ??
        new Date().toISOString();

    const rawFechaFin = r.fechaFin ?? r.FechaFin ?? rawFechaInicio;

    const fecha = normalizeDate(rawFechaInicio);
    const fechaFin = normalizeDate(rawFechaFin);

    // --- 2) Normalizar hora: usar solo HH:mm de horaInicio/horaFin ---
    const normalizeHour = (val) => {
        if (!val) return "00:00";
        const s = String(val);
        return s.slice(0, 5);
    };

    const horaInicio = normalizeHour(r.horaInicio ?? r.HoraInicio ?? "00:00");
    const horaFin = normalizeHour(r.horaFin ?? r.HoraFin ?? "00:00");

    // --- 3) Construimos las fechas completas ---
    const start = toDateWithTime(fecha, horaInicio);
    const end = toDateWithTime(fechaFin, horaFin);

    const origenTexto = r.origenTexto ?? r.OrigenTexto ?? "";
    const destinoTexto = r.destinoTexto ?? r.DestinoTexto ?? "";

    const origin = parseJsonLocation(origenTexto);
    const destination = parseJsonLocation(destinoTexto);

    const idEstado = Number(
        r.idEstadoReserva ?? r.IdEstadoReserva ?? r.estado ?? r.Estado ?? 1
    );
    const estadoInfo = getEstadoInfo(idEstado);

    const meta = {
        idReserva: id,
        idSolicitante: r.idSolicitante ?? r.IdSolicitante ?? null,
        idChofer: r.idChofer ?? r.IdChofer ?? null,
        motivo: r.motivo ?? r.Motivo ?? "",
        observaciones: r.observaciones ?? r.Observaciones ?? "",
        origin,
        destination,
        estadoId: idEstado,
        estadoKey: estadoInfo.key,
        estadoLabel: estadoInfo.label,
        chofer: r.chofer ?? r.nombreChofer ?? r.NombreChofer ?? "",
        decidedAt: r.decidedAt ?? null,
        rhComment: r.rhComment ?? "",
        metaHidden: false,
    };

    return {
        id,
        start,
        end,
        title: meta.motivo || "Reserva",
        meta,
    };
}
